import { MiniSensorType, MINI_SENSOR_TYPE_INFO } from './sensors';
import type { MiniSensorTypeInfo } from './sensors';
import type { BaseSensorConstraints } from '../common/constraints';
import { GAS_CONC_SCALE, NO_SENSOR_CONSTRAINTS } from '../common/constraints';
import { GasConcUnit } from '../common/gas_conc_unit';

// ============================================================================
// Mini 센서 기능 플래그 (Lite의 SensorFeatureFlags와 동일한 패턴)
// ============================================================================

interface MiniSensorFeatureFlags {
  has_twa_stel: boolean;
  enable_auto_fresh: boolean;
  enable_bump_test_threshold: boolean; // Mini는 항상 false (Lite 전용 기능)
}

type MiniSensorConstraints = BaseSensorConstraints & {
  features: MiniSensorFeatureFlags;
};

// ============================================================================
// Mini 센서별 제약조건 상수
// ============================================================================

// O2 센서 제약조건 - Mini
const O2_MINI_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 18000,
    ranges: [[0, 20900]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 19500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 23500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.VOL,
  twa_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// CO 센서 제약조건 - Mini
const CO_MINI_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 100000,
    ranges: [[10000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 30000,
    ranges: [[10000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 100000,
    ranges: [[10000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 30000,
    ranges: [[10000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 200000,
    ranges: [[10000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// H2S 센서 제약조건 - Mini
const H2S_MINI_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 25000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 10000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 15000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 10000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 15000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// O2LF (Low Flow O2) 센서 제약조건 - Mini
const O2LF_MINI_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 18000,
    ranges: [[0, 20900]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 19500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 23500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.VOL,
  twa_alarm: { default: 0, ranges: [[0, 100]], scale: GAS_CONC_SCALE },
  stel_alarm: { default: 0, ranges: [[0, 100]], scale: GAS_CONC_SCALE },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// CO2 센서 제약조건 (PPM) - Mini
const CO2_MINI_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 5000000,
    ranges: [[5000000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 5000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 15000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 5000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 15000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// H2 센서 제약조건
const H2_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 100000,
    ranges: [[30000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 100000,
    ranges: [[30000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 500000,
    ranges: [[30000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// NH3 센서 제약조건
const NH3_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 50000,
    ranges: [[6000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 25000,
    ranges: [[6000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 35000,
    ranges: [[6000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 25000,
    ranges: [[6000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 35000,
    ranges: [[6000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// Cl2 센서 제약조건
const CL2_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[200, 50000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 500,
    ranges: [[200, 50000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 1000,
    ranges: [[200, 50000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 500,
    ranges: [[200, 50000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 1000,
    ranges: [[200, 50000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// SO2 센서 제약조건
const SO2_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[300, 20000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 2000,
    ranges: [[300, 20000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 5000,
    ranges: [[300, 20000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 2000,
    ranges: [[300, 20000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 5000,
    ranges: [[300, 20000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// NO2 센서 제약조건
const NO2_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[500, 30000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 2000,
    ranges: [[500, 30000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 5000,
    ranges: [[500, 30000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 500,
    ranges: [[500, 30000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 1000,
    ranges: [[500, 30000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// HCN 센서 제약조건
const HCN_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[900, 50000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 2500,
    ranges: [[900, 50000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 4700,
    ranges: [[900, 50000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 2500,
    ranges: [[900, 50000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 4700,
    ranges: [[900, 50000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// PH3 센서 제약조건
const PH3_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 1000,
    ranges: [[100, 20000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 300,
    ranges: [[100, 20000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 1000,
    ranges: [[100, 20000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 300,
    ranges: [[100, 20000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 1000,
    ranges: [[100, 20000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// ETO 센서 제약조건
const ETO_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[500, 100000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 1000,
    ranges: [[500, 100000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 5000,
    ranges: [[500, 100000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 1000,
    ranges: [[500, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 5000,
    ranges: [[500, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// O3 센서 제약조건
const O3_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 800,
    ranges: [[60, 5000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 100,
    ranges: [[60, 5000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 200,
    ranges: [[60, 5000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 100,
    ranges: [[60, 5000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 300,
    ranges: [[60, 5000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// HCl 센서 제약조건
const HCL_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 10000,
    ranges: [[600, 15000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 1000,
    ranges: [[600, 15000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 2000,
    ranges: [[600, 15000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 1000,
    ranges: [[600, 15000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 2000,
    ranges: [[600, 15000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// NO 센서 제약조건
const NO_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 50000,
    ranges: [[2000, 250000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 25000,
    ranges: [[2000, 250000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 50000,
    ranges: [[2000, 250000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 25000,
    ranges: [[2000, 250000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 50000,
    ranges: [[2000, 250000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// NH3_E 센서 제약조건
const NH3_E_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 50000,
    ranges: [[6000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 25000,
    ranges: [[6000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 35000,
    ranges: [[6000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 25000,
    ranges: [[6000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 35000,
    ranges: [[6000, 1000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// CO_H 센서 제약조건
const CO_H_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 100000,
    ranges: [[10000, 500000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 30000,
    ranges: [[10000, 500000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 100000,
    ranges: [[10000, 500000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 30000,
    ranges: [[10000, 500000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 200000,
    ranges: [[10000, 500000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// CH2O2 센서 제약조건 (현재 미사용/0으로 설정)
const CH2O2_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: { default: 0, ranges: [[0, 0]], scale: 1 },
  low_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  high_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// CO_E 센서 제약조건
const CO_E_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 100000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 30000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 100000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 30000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 200000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// 센서별 제약조건 매핑 (Mini은 단일 센서)
export const SENSOR_CONSTRAINTS: Record<MiniSensorType, MiniSensorConstraints> = {
  [MiniSensorType.CO]: {
    ...CO_MINI_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.O2]: {
    ...O2_MINI_CONSTRAINTS,
    features: { has_twa_stel: false, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.H2]: {
    ...H2_CONSTRAINTS,
    features: { has_twa_stel: false, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.H2S]: {
    ...H2S_MINI_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.NH3]: {
    ...NH3_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.Cl2]: {
    ...CL2_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.SO2]: {
    ...SO2_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.NO2]: {
    ...NO2_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.HCN]: {
    ...HCN_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.PH3]: {
    ...PH3_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.ETO]: {
    ...ETO_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.O3]: {
    ...O3_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.HCl]: {
    ...HCL_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.NO]: {
    ...NO_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.NH3_E]: {
    ...NH3_E_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.O2_LF]: {
    ...O2LF_MINI_CONSTRAINTS,
    features: { has_twa_stel: false, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.CO_H]: {
    ...CO_H_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.CH2O2]: {
    ...CH2O2_CONSTRAINTS,
    features: { has_twa_stel: false, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.CO_E]: {
    ...CO_E_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: true, enable_bump_test_threshold: false },
  },
  [MiniSensorType.CO2]: {
    ...CO2_MINI_CONSTRAINTS,
    features: { has_twa_stel: true, enable_auto_fresh: false, enable_bump_test_threshold: false },
  },
  [MiniSensorType.NO_SENSOR]: {
    ...NO_SENSOR_CONSTRAINTS,
    features: { has_twa_stel: false, enable_auto_fresh: false, enable_bump_test_threshold: false },
  },
};

// Mini 센서별 기본값 생성 함수
export const getMiniSensorDefaultValues = (sensorType: MiniSensorType) => {
  const constraints = SENSOR_CONSTRAINTS[sensorType];
  if (!constraints) {
    return {
      has_twa_stel: false,
      enable_auto_fresh: false,
      calibration_gas_conc: 0,
      calibration_interval: 0,
      bump_interval: 180,
      low_alarm: 0,
      high_alarm: 0,
      twa_alarm: 0,
      stel_alarm: 0,
      stel_period: 0,
      twa_period: 0,
    };
  }

  return {
    has_twa_stel: constraints.features.has_twa_stel,
    enable_auto_fresh: constraints.features.enable_auto_fresh,
    calibration_gas_conc:
      (constraints.calibration_gas_conc?.default || 0) /
      (constraints.calibration_gas_conc?.scale || 1),
    calibration_interval: constraints.calibration_interval?.default || 0,
    bump_interval: constraints.bump_interval?.default || 180,
    low_alarm: (constraints.low_alarm?.default || 0) / (constraints.low_alarm?.scale || 1),
    high_alarm: (constraints.high_alarm?.default || 0) / (constraints.high_alarm?.scale || 1),
    twa_alarm: (constraints.twa_alarm?.default || 0) / (constraints.twa_alarm?.scale || 1),
    stel_alarm: (constraints.stel_alarm?.default || 0) / (constraints.stel_alarm?.scale || 1),
    stel_period: constraints.stel_period?.default || 0,
    twa_period: constraints.twa_period?.default || 0,
  };
};

// Lite의 getSensorTypeInfo()와 동일한 패턴 — base info + features 병합
export const getMiniSensorInfo = (
  sensorType: MiniSensorType
): MiniSensorTypeInfo & MiniSensorFeatureFlags => {
  const baseInfo = MINI_SENSOR_TYPE_INFO[sensorType] ?? { index: -1, label: '-' };
  const features = SENSOR_CONSTRAINTS[sensorType]?.features ?? {
    has_twa_stel: false,
    enable_auto_fresh: false,
    enable_bump_test_threshold: false,
  };
  return { ...baseInfo, ...features };
};
